import React from 'react';
import './HistoryLegendMolecule.scss';

export interface HistoryLegendItem {
  /** CSS color value (or `var(--token)` reference) for the swatch fill. Use `'transparent'` for empty states. */
  tokenSwatch: string;
  /** Optional CSS color for a solid border on the swatch. Use with transparent `tokenSwatch` for dashed "not due" rendering. */
  tokenBorder?: string;
  /** Pre-localized label. Consumer must supply translated copy. */
  label: string;
}

export interface HistoryLegendMoleculeProps {
  /** Legend entries in display order. */
  items: HistoryLegendItem[];
  /** Accessible label for the legend region. */
  ariaLabel?: string;
  className?: string;
}

const HistoryLegendMolecule: React.FC<HistoryLegendMoleculeProps> = ({
  items,
  ariaLabel,
  className = '',
}) => {
  if (!items || items.length === 0) {
    return null;
  }

  const classes = ['history-legend-molecule', className]
    .filter(Boolean)
    .join(' ');

  return (
    <ul className={classes} aria-label={ariaLabel}>
      {items.map((item, index) => {
        const isTransparent =
          item.tokenSwatch === 'transparent' || item.tokenSwatch === 'none';
        const swatchClass = [
          'history-legend-molecule__swatch',
          isTransparent
            ? 'history-legend-molecule__swatch--transparent'
            : 'history-legend-molecule__swatch--filled',
        ].join(' ');

        const swatchStyle: React.CSSProperties = {
          backgroundColor: item.tokenSwatch,
        };
        if (item.tokenBorder) {
          swatchStyle.borderWidth = '1.5px';
          swatchStyle.borderStyle = isTransparent ? 'dashed' : 'solid';
          swatchStyle.borderColor = item.tokenBorder;
        }

        return (
          <li
            key={`${item.label}-${index}`}
            className="history-legend-molecule__item"
          >
            <span
              className={swatchClass}
              style={swatchStyle}
              aria-hidden="true"
            />
            <span className="history-legend-molecule__label">{item.label}</span>
          </li>
        );
      })}
    </ul>
  );
};

HistoryLegendMolecule.displayName = 'HistoryLegendMolecule';

export default HistoryLegendMolecule;
